import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CheckCircle2, Gift } from "lucide-react";
import { toast } from "sonner";
import AuthShell from "@/components/AuthShell";
import OtpField from "@/components/auth/OtpField";
import SignupDetailsForm, { joiningBonusHint } from "@/components/auth/SignupDetailsForm";
import PendingApprovalCard from "@/components/auth/PendingApprovalCard";

const EMPTY_FORM = { name: "", email: "", password: "", phone: "", business_category: "", business_location: "", referral_code: "" };

export default function Signup() {
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") === "business" ? "business" : "guest");
  const [step, setStep] = useState("phone"); // phone -> otp -> details
  const [form, setForm] = useState({ ...EMPTY_FORM, referral_code: (params.get("ref") || "").trim().toUpperCase() });
  const [refCheck, setRefCheck] = useState(null); // {valid, referrer_first_name, message}
  const [otp, setOtp] = useState("");
  const [demoOtp, setDemoOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  const [categories, setCategories] = useState([]);
  const { signupPhone } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/public/categories").then((r) => setCategories(r.data || [])).catch(() => {});
  }, []);

  // Validate the referral code against the public endpoint (on prefill + blur).
  const checkReferral = useCallback(async (raw) => {
    const code = (raw || "").trim();
    if (!code) { setRefCheck(null); return; }
    try {
      const { data } = await api.get(`/public/referral/${encodeURIComponent(code)}`);
      setRefCheck(data);
    } catch (err) {
      setRefCheck({ valid: false, message: err?.response?.data?.detail || "Could not check this referral code" });
    }
  }, []);
  useEffect(() => {
    const ref = params.get("ref");
    if (ref) checkReferral(ref);
  }, [params, checkReferral]);

  const switchRole = (next) => {
    if (next === role) return;
    setRole(next);
    setStep("phone");
    setOtp(""); setDemoOtp("");
    setForm((f) => ({ ...EMPTY_FORM, referral_code: f.referral_code }));
  };

  useEffect(() => {
    if (role === "business" && !form.business_category && categories.length) {
      setForm((f) => ({ ...f, business_category: categories[0].name }));
    }
  }, [role, categories, form.business_category]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const requestOtp = useCallback(async (e) => {
    e?.preventDefault?.();
    if (!form.phone) return toast.error("Enter your mobile number");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/phone/request-otp", { phone: form.phone, purpose: "signup" });
      if (data.demo_otp) {
        setDemoOtp(data.demo_otp);
        toast.success("On-screen OTP mode — your code is shown below the field");
      } else {
        toast.success("OTP sent to your phone");
      }
      setStep("otp");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send OTP");
    } finally { setBusy(false); }
  }, [form.phone]);

  // The backend re-checks the code on signup, so this only advances the wizard
  const verifyOtp = (e) => {
    e?.preventDefault?.();
    if (!otp || otp.length !== 6) return toast.error("Enter the 6-digit OTP");
    setStep("details");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await signupPhone({
        phone: form.phone,
        otp_code: otp,
        role,
        name: form.name,
        password: form.password || undefined,
        email: form.email || undefined,
        business_category: form.business_category || undefined,
        business_location: form.business_location || undefined,
        referral_code: role === "guest" && form.referral_code.trim() ? form.referral_code.trim() : undefined,
      });
      if (data.pending_approval) {
        setPending({ name: form.name, email: form.email || "" });
        toast.info("Application received — we'll email you once approved");
      } else if (data.token && data.user) {
        toast.success(`Welcome, ${data.user.name}`);
        nav("/guest");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Sign up failed";
      toast.error(detail);
      if (/referral code/i.test(detail)) setRefCheck({ valid: false, message: detail });
      // If the OTP was consumed but the rest failed, walk the user back
      if (/otp|expired/i.test(err?.response?.data?.detail || "")) {
        setStep("phone"); setOtp(""); setDemoOtp("");
      }
    } finally {
      setBusy(false);
    }
  };

  if (pending) {
    return (
      <PendingApprovalCard name={pending.name} email={pending.email}
                           onHome={() => nav("/")} onLogin={() => nav("/login")} />
    );
  }

  return (
    <AuthShell
      heading="Join the pool."
      subhead="Verify your mobile, fill in a few details, and you're in. We send a 6-digit OTP so no one else can use your number."
      footerTag="valuepool.app / v1"
    >
      <Card className="w-full max-w-md p-8 border border-border bg-card shadow-sm" data-testid="signup-card">
        <h2 className="font-heading text-2xl font-semibold tracking-tight">Create account</h2>
        <p className="text-muted-foreground text-sm mt-1">Pick the side you want to be on.</p>

        <Tabs value={role} onValueChange={switchRole} className="mt-5">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger data-testid="role-guest" value="guest">Guest</TabsTrigger>
            <TabsTrigger data-testid="role-business" value="business">Business</TabsTrigger>
          </TabsList>
        </Tabs>

        {role === "business" && (
          <div className="mt-4 rounded-lg bg-muted/60 border border-border p-3 text-xs text-muted-foreground flex gap-2">
            <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <span>Business accounts go through a quick review. You'll be notified once approved.</span>
          </div>
        )}

        {role === "guest" && refCheck?.valid && step !== "details" && (
          <div className="mt-4 rounded-lg bg-secondary/10 border border-secondary/30 p-3 text-xs text-secondary flex gap-2" data-testid="signup-referral-banner">
            <Gift className="w-4 h-4 shrink-0" />
            <span>Referred by <b>{refCheck.referrer_first_name}</b> · code {form.referral_code}
              {joiningBonusHint(refCheck) && <span className="block mt-0.5" data-testid="signup-banner-joining-bonus">{joiningBonusHint(refCheck)}</span>}
            </span>
          </div>
        )}

        <div className="mt-5 flex items-center gap-2 text-xs text-muted-foreground" data-testid="signup-step">
          <span className={step === "phone" ? "text-primary font-semibold" : ""}>1. Phone</span>
          <span>›</span>
          <span className={step === "otp" ? "text-primary font-semibold" : ""}>2. Verify</span>
          <span>›</span>
          <span className={step === "details" ? "text-primary font-semibold" : ""}>3. Details</span>
        </div>

        {step === "phone" && (
          <form className="mt-5 space-y-4" onSubmit={requestOtp}>
            <div>
              <Label>Mobile number</Label>
              <Input data-testid="signup-phone" value={form.phone} onChange={update("phone")} required
                     placeholder="Your mobile number" />
            </div>
            <Button data-testid="signup-otp-request" disabled={busy} type="submit" className="w-full bg-primary text-primary-foreground">
              {busy ? "Sending OTP…" : "Send OTP"}
            </Button>
          </form>
        )}

        {step === "otp" && (
          <form className="mt-5 space-y-4" onSubmit={verifyOtp}>
            <OtpField value={otp} onChange={setOtp} demoOtp={demoOtp} placeholder="123456"
                      testId="signup-otp" demoTestId="signup-demo-otp"
                      note="— SMS delivery unavailable, on-screen OTP is enabled." />
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setStep("phone")}>Back</Button>
              <Button type="button" variant="ghost" size="sm" onClick={requestOtp} disabled={busy} data-testid="signup-otp-resend">
                Resend OTP
              </Button>
              <Button data-testid="signup-otp-verify" disabled={busy} type="submit" className="ml-auto bg-primary text-primary-foreground">
                Verify
              </Button>
            </div>
          </form>
        )}

        {step === "details" && (
          <SignupDetailsForm role={role} form={form} update={update} setForm={setForm}
                             categories={categories} busy={busy} onSubmit={submit}
                             refCheck={refCheck} onReferralBlur={() => checkReferral(form.referral_code)}
                             onReferralChange={() => setRefCheck(null)} />
        )}

        <div className="mt-6 text-sm text-muted-foreground">
          Already have one? <Link to="/login" className="text-primary font-medium hover:underline">Log in</Link>
        </div>
      </Card>
    </AuthShell>
  );
}
